import { type ReactNode, useEffect, useState } from "react";
import { useApiQuery } from "@/api/useApi";
import type { ExpensePolicy } from "@/api/types";
import { Icon, type IconName } from "@/components/Icon";
import {
  type InstallBrowser,
  type InstallOffer,
  type IosBrowser,
  installPointer,
  iosBrowser,
  isMobileDevice,
  lastAsked,
  markAsked,
  shouldAsk,
  useInstallOffer,
} from "@/install";

interface Step {
  icon: IconName;
  text: ReactNode;
}

/** Where each browser keeps installing, for when it isn't offering its own install dialog. */
const browserSteps: Record<InstallBrowser, Step[]> = {
  edge: [
    {
      icon: "download",
      text: (
        <>
          Click <strong>App available</strong> at the right of the address bar
        </>
      ),
    },
    {
      icon: "addSquare",
      text: (
        <>
          Click <strong>Install</strong>
        </>
      ),
    },
  ],
  chrome: [
    {
      icon: "download",
      text: (
        <>
          Click the <strong>Install</strong> icon at the right of the address bar
        </>
      ),
    },
    {
      icon: "addSquare",
      text: (
        <>
          Click <strong>Install</strong>
        </>
      ),
    },
  ],
  android: [
    {
      icon: "chevronDown",
      text: (
        <>
          Tap the <strong>⋮</strong> menu
        </>
      ),
    },
    {
      icon: "addSquare",
      text: (
        <>
          Tap <strong>Add to Home screen</strong>, then <strong>Install</strong>
        </>
      ),
    },
  ],
  "mac-safari": [
    {
      icon: "chevronDown",
      text: (
        <>
          Click <strong>File</strong> in the menu bar
        </>
      ),
    },
    {
      icon: "addSquare",
      text: (
        <>
          Click <strong>Add to Dock</strong>, then <strong>Add</strong>
        </>
      ),
    },
  ],
};

const addStep: Step = {
  icon: "addSquare",
  text: (
    <>
      <strong>Add to Home Screen</strong>, then <strong>Add</strong>
    </>
  ),
};

const viewMoreStep: Step = {
  icon: "chevronDown",
  text: (
    <>
      Click <strong>View More</strong>
    </>
  ),
};

/** Adding to the Home Screen on an iPhone or iPad, in each browser. */
const iosSteps: Record<IosBrowser, Step[]> = {
  safari: [
    {
      icon: "share",
      text: (
        <>
          Click <strong>Share</strong>
        </>
      ),
    },
    viewMoreStep,
    addStep,
  ],
  chrome: [
    {
      icon: "share",
      text: (
        <>
          Tap <strong>Share</strong> in the address bar
        </>
      ),
    },
    viewMoreStep,
    addStep,
  ],
  firefox: [
    {
      icon: "share",
      text: (
        <>
          Tap <strong>Share</strong> in the address bar
        </>
      ),
    },
    viewMoreStep,
    addStep,
  ],
  edge: [
    {
      icon: "chevronDown",
      text: (
        <>
          Tap the <strong>⋯</strong> menu
        </>
      ),
    },
    {
      icon: "share",
      text: (
        <>
          Tap <strong>Share</strong>
        </>
      ),
    },
    addStep,
  ],
};

/** Where the browser keeps it, when it isn't in the address bar. */
const menuHint: Partial<Record<InstallBrowser, ReactNode>> = {
  edge: (
    <>
      Not there? Open the <strong>⋯</strong> menu, then <strong>Apps</strong>, then{" "}
      <strong>Install this site as an app</strong>.
    </>
  ),
  chrome: (
    <>
      Not there? Open the <strong>⋮</strong> menu, then <strong>Cast, save, and share</strong>, then{" "}
      <strong>Install page as app</strong>.
    </>
  ),
};

/** How to install on this device: a button where the browser can do it, otherwise the steps. */
export function InstallSteps({
  offer,
  onDone,
}: {
  offer: NonNullable<InstallOffer>;
  /** After the browser's install dialog closes, installed or not: the menu steps that would
   *  replace the button don't apply then. */
  onDone?: () => void;
}) {
  const [busy, setBusy] = useState(false);

  if (offer.kind === "button") {
    return (
      <button
        type="button"
        className="btn"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void offer.install().finally(() => {
            setBusy(false);
            onDone?.();
          });
        }}
      >
        <Icon name="download" size={18} /> Install
      </button>
    );
  }

  const steps = offer.kind === "ios" ? iosSteps[iosBrowser()] : browserSteps[offer.browser];
  const hint = offer.kind === "browser" ? menuHint[offer.browser] : undefined;
  return (
    <>
      <ol className="install-tiles">
        {steps.map((step, index) => (
          <li key={step.icon + String(index)}>
            <span className="install-step">{index + 1}</span>
            <span className={`install-art ${step.icon === "share" ? "share" : ""}`}>
              <Icon name={step.icon} size={30} />
            </span>
            <span>{step.text}</span>
          </li>
        ))}
      </ol>
      <p className="muted small install-note">
        {hint ?? (
          <>
            Once added, <strong>PA Expenses</strong> opens like an app from your Home Screen.
          </>
        )}
      </p>
    </>
  );
}

/**
 * Asks to install the app on a phone or tablet, on any page: the first time someone uses it on
 * the device, then again every few days (set in Settings) until it's installed. The × puts it off
 * until then. Computers aren't asked; the account menu's "Install on this device" is there.
 *
 * On Android it asks only when Chrome offers its own Install dialog. When Chrome doesn't (the
 * app is installed already, or Chrome is showing its own prompt), the menu steps don't apply.
 */
export function InstallPrompt() {
  const offer = useInstallOffer();
  const { data: policy } = useApiQuery<ExpensePolicy>("/api/expenses/policy");
  if (!offer || offer.kind === "browser" || !policy || !isMobileDevice()) return null;
  return <InstallPopup offer={offer} everyDays={policy.installPromptEveryDays ?? 3} />;
}

function InstallPopup({
  offer,
  everyDays,
}: {
  offer: NonNullable<InstallOffer>;
  everyDays: number;
}) {
  // Decided once, when the app opens; asking counts from when it was shown.
  const [open, setOpen] = useState(() => shouldAsk(lastAsked(), Date.now(), everyDays));
  useEffect(() => {
    if (open) markAsked();
  }, [open]);

  if (!open) return null;

  const pointsAt = offer.kind === "ios" ? installPointer() : "none";
  const close = () => setOpen(false);

  return (
    <>
      {/* Dims the page so the prompt stands out; tapping it closes the prompt like the ×. */}
      <div className="install-backdrop" aria-hidden="true" onClick={close} />
      <section className={`install-pop points-${pointsAt}`} aria-label="Install PA Expenses">
        <div className="install-head">
          <span className="install-badge" aria-hidden="true">
            <span className="install-rays" />
            <Icon name="addSquare" size={30} />
          </span>
          <div>
            <h2>{offer.kind === "ios" ? "Add to Home Screen" : "Install PA Expenses"}</h2>
            <p className="muted">For faster access and better performance.</p>
          </div>
          <button type="button" className="install-close" aria-label="Not now" onClick={close}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <InstallSteps offer={offer} onDone={close} />
      </section>
    </>
  );
}
